import { useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useLocation, useNavigate } from 'react-router-dom'
import { BeoflowButton } from '@/components/BeoflowButton'
import { BeoflowCard } from '@/components/BeoflowCard'
import { BeoflowIndicator } from '@/components/BeoflowIndicator'
import { useSubscription } from '@/services/api/settings'
import {
  dismissGraceBannerForSession,
  GRACE_BANNER_DISMISSED_KEY,
} from '@/lib/subscriptionAccess'
import { colors } from '@/theme/BeoflowColors.generated'
import { indicatorSpec } from '@/theme/BeoflowIndicatorSpec.generated'
import { layout, spacing } from '@/theme/BeoflowSpacingLayout.generated'
import { fontStackFor } from '@/theme/muiTheme'
import { textStyles } from '@/theme/BeoflowTypography.generated'

const sectionHeaderStyle = {
  fontFamily: fontStackFor(textStyles.sectionHeader.fontFamily),
  fontSize: `${textStyles.sectionHeader.fontSize}px`,
  fontWeight: textStyles.sectionHeader.fontWeight,
  letterSpacing: `${textStyles.sectionHeader.letterSpacing}px`,
  textTransform: textStyles.sectionHeader.textTransform,
} as const
const tokenPx = (value: number) => `${value}px`

function wasDismissedForSession(): boolean {
  try { return sessionStorage.getItem(GRACE_BANNER_DISMISSED_KEY) === '1' } catch { return false }
}

export function PaidGraceBanner() {
  const { data: subscription } = useSubscription()
  const location = useLocation()
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(wasDismissedForSession)
  const inGrace =
    subscription?.access_state === 'paid_grace' ||
    subscription?.is_in_grace_period

  if (!inGrace || dismissed) return null

  const renew = () => {
    const returnTo = `${location.pathname}${location.search}`
    navigate(`/paywall?mode=renew&returnTo=${encodeURIComponent(returnTo)}`)
  }

  const dismiss = () => {
    dismissGraceBannerForSession()
    setDismissed(true)
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: layout.navigationBarHeight + spacing.md + spacing.sm,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.modal - 1,
        px: { xs: tokenPx(spacing.md), md: tokenPx(spacing.lg) },
        pointerEvents: 'none',
      }}
    >
      <BeoflowCard
        role="status"
        sx={{
          maxWidth: layout.maxContentWidth + (layout.iconXLarge * 4),
          mx: 'auto',
          py: tokenPx(spacing.md),
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1fr) auto auto' },
          gridTemplateAreas: { xs: '"content dismiss" "renew renew"', sm: '"content renew dismiss"' },
          alignItems: 'center',
          gap: tokenPx(spacing.md),
          pointerEvents: 'auto',
        }}
      >
        <Box sx={{ gridArea: 'content', display: 'flex', alignItems: 'flex-start', gap: tokenPx(spacing.md), minWidth: 0 }}>
          <BeoflowIndicator direction="alert" color={colors.peakOrange} size={indicatorSpec.sizes.small.dimension} decorative />
          <Box>
            <Typography
              sx={sectionHeaderStyle}
            >
              GRACE ACCESS
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Renew before access pauses. Your training flow stays available during grace.
            </Typography>
          </Box>
        </Box>
        <BeoflowButton
          variant="outline"
          size="small"
          onClick={renew}
          sx={{ gridArea: 'renew', minHeight: layout.navigationBarHeight, width: { xs: '100%', sm: 'auto' } }}
        >
          RENEW
        </BeoflowButton>
        <IconButton
          aria-label="Dismiss renewal reminder"
          size="small"
          onClick={dismiss}
          sx={{
            gridArea: 'dismiss',
            color: 'text.secondary',
            minWidth: layout.navigationBarHeight,
            minHeight: layout.navigationBarHeight,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </BeoflowCard>
    </Box>
  )
}
